import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { BillingGroupName, useBilling } from 'services/repositories/billing/billing';
import { ConnectionsFilterKey } from 'services/repositories/connections/connections';
import { useConnectionsStats } from 'services/repositories/stats/stats';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { LoadingPage } from 'components/Loading/LoadingPage';
import { Page } from 'components/Page/Page';

import { SettingsSidebar } from '../SettingsSidebar/SettingsSidebar';
import { ActiveModulesSection, PeriodMonths } from './components/ActiveModulesSection';
import { ModuleDetailSlideOver } from './components/ModuleDetailSlideOver';
import { UsageSection } from './components/UsageSection';

export const UsageAndModules = () => {
  const { t } = useTranslation();
  const [months, setMonths] = useState<PeriodMonths>(6);
  const [selectedGroupName, setSelectedGroupName] = useState<BillingGroupName | null>(null);

  const {
    data: billing,
    isLoading: isBillingLoading,
    isError: isBillingError,
    refetch: refetchBilling,
  } = useBilling({ variables: { months } });
  const {
    data: connectionsStats,
    isLoading: isConnectionsLoading,
    isError: isConnectionsError,
    refetch: refetchConnections,
  } = useConnectionsStats({
    variables: {
      query: {
        filterValues: [{ key: ConnectionsFilterKey.status, value: 'connected' }],
      },
    },
  });

  const isLoading = isBillingLoading || isConnectionsLoading;
  const isError = isBillingError || isConnectionsError;
  const handleRetry = () => {
    if (isBillingError) {
      refetchBilling();
    }
    if (isConnectionsError) {
      refetchConnections();
    }
  };

  const {
    credit_allowance: creditAllowance = 0,
    history = [],
    usage = [],
    used_credits: usedCredits = 0,
  } = billing ?? {};
  const {
    connectedPartnersCount = connectionsStats?.connectionsCount ?? 0,
    readyRelationshipsCount: readyConnectionsCount = 0,
  } = connectionsStats ?? {};

  // t('settings:usageAndModules.featureGroups.agentic_primitives')
  // t('settings:usageAndModules.featureGroups.custom_transformations')
  // t('settings:usageAndModules.featureGroups.document_agents')

  return (
    <>
      <Page>
        <Page.Head title={t('common:mainMenu.settings')} />

        <Page.Section leftAside={<SettingsSidebar />}>
          {isLoading ? (
            <LoadingPage />
          ) : isError ? (
            <EmptyState
              icon={<ExclamationCircleIcon />}
              title={t('settings:usageAndModules.error.title')}
              description={t('settings:usageAndModules.error.description') ?? undefined}
              ctaText={t('settings:usageAndModules.error.retry') ?? undefined}
              ctaOnClick={handleRetry}
            />
          ) : (
            <div className="flex w-full flex-col divide-y divide-gray-200 *:py-6 *:first:pt-0 *:last:pb-0">
              <UsageSection
                connectedPartnersCount={connectedPartnersCount}
                readyConnectionsCount={readyConnectionsCount}
                usedCredits={usedCredits}
                creditAllowance={creditAllowance}
              />

              <ActiveModulesSection
                months={months}
                onMonthsChange={setMonths}
                history={history}
                usage={usage}
                onGroupSelect={setSelectedGroupName}
              />
            </div>
          )}
        </Page.Section>
      </Page>

      <ModuleDetailSlideOver
        groupName={selectedGroupName}
        onClose={() => setSelectedGroupName(null)}
        initialMonths={months}
      />
    </>
  );
};
